import { useDashboardPermissions } from "@/permissions/useDashboardPermissions";
import { useActivitiesPermissions } from "@/permissions/useActivitiesPermissions";
import { useMeetingPermissions } from "@/permissions/useMeetingPermissions";
import { useNewsPermissions } from "@/permissions/useNewsPermissions";
import { useMealsPermissions } from "@/permissions/useMealsPermissions";
import { usePlannerPermissions } from "@/permissions/usePlannerPermissions";
import type { PermissionActions, PermissionSubjects } from "@/types";
import { defineStore } from "pinia";
import type { Ref } from "vue";
import { useRouter } from "vue-router";

type PermissionChecker = Ref<boolean>;

export const useCan = defineStore("useCan", () => {
  const router = useRouter();

  const PERMISSION_MAP: Record<
    PermissionSubjects,
    Partial<Record<PermissionActions, PermissionChecker>>
  > = {
    planner: usePlannerPermissions(),
    dashboard: useDashboardPermissions(),
    meeting: useMeetingPermissions(),
    news: useNewsPermissions(),
    activities: useActivitiesPermissions(),
    meals: useMealsPermissions(),
  };

  const can = (
    action: PermissionActions,
    subject: PermissionSubjects,
  ): Ref<boolean> => {
    const checker = PERMISSION_MAP[subject][action];

    if (checker !== undefined) {
      return checker;
    }

    throw new Error(`Undefined permission check: ${action}@${subject}`);
  };

  const shouldHave = (
    action: PermissionActions,
    subject: PermissionSubjects,
  ) => {
    if (can(action, subject).value === false) {
      router.push("/");
    }
  };

  return {
    can,
    shouldHave,
  };
});
